// SidePanel — Session B groundwork for Session C's edit-in-place.
//
// A right-hand slide-over used to open a record without leaving the list.
// In Session B it hosts read-only summaries; Session C puts forms in it.
//
// Motion: a drawer earns a standard entrance (200ms, foundation ease-out,
// transform-only). Exits are instant-feeling (150ms). Honors
// prefers-reduced-motion via CSS. Esc and overlay click both close.
import { ReactNode, useEffect } from "react";

type Props = {
  open: boolean;
  title: string;
  onClose: () => void;
  /** Footer slot — actions like "Edit full record". */
  footer?: ReactNode;
  children: ReactNode;
};

export default function SidePanel({ open, title, onClose, footer, children }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <div
      className={`fw-sidepanel${open ? " fw-sidepanel--open" : ""}`}
      aria-hidden={!open}
    >
      <div className="fw-sidepanel__backdrop" onClick={onClose} />
      <aside
        className="fw-sidepanel__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="fw-sidepanel__head">
          <h2>{title}</h2>
          <button
            type="button"
            className="fw-sidepanel__close"
            onClick={onClose}
            aria-label="Close panel"
          >
            <i className="ri-close-line" />
          </button>
        </header>
        <div className="fw-sidepanel__body">{children}</div>
        {footer && <footer className="fw-sidepanel__foot">{footer}</footer>}
      </aside>
    </div>
  );
}

/** Label/value row for read-only record summaries. */
export function PanelField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="fw-sidepanel__field">
      <span className="fw-sidepanel__label">{label}</span>
      <span className="fw-sidepanel__value">{children}</span>
    </div>
  );
}
